/* eslint-disable import/no-extraneous-dependencies */
import * as React from 'react';
import { InsuranceOffer } from 'types/Product';
import styled from 'styled-components';
import { TypographyComponent as TypographyElement, Radio, RadioGroup } from '@tvg/fuse-design';
import { getPriceWithSymbol } from 'utilities/price/price-utilities';
import getInsuranceMaxWeeklyPrice from 'utilities/insurance/insurance-utilities';
import { enableMonthlyProtectInsuranceTempOffer } from 'constants/promotion-voucher-constants';
import { getSite } from 'context/SiteContext';
import BrandTypes from 'constants/brand';

const InsuranceRadio = styled(Radio)`
  align-items: baseline;
  margin-bottom: var(--spacing-space16) !important;
`;

const StyledTypography = styled(TypographyElement)`
  margin-bottom: var(--spacing-space16);
`;

const LabelTextContainer = styled.span`
  display: flex;
  flex-direction: column;

  > * {
    margin-bottom: 0;
  }
`;

interface InsuranceSelectorProps {
  options?: InsuranceOffer[] | null;
  selectedInsurance?: InsuranceOffer | string | null;
  handleSelectedInsurance: (option: InsuranceOffer | null) => void;
  handleNoInsuranceSelect: () => void;
  type?: string;
  showNoInsuranceRadio?: boolean;
  skuId: string;
}

export function InsuranceSelector({
  options,
  handleSelectedInsurance,
  handleNoInsuranceSelect,
  selectedInsurance,
  type = '',
  showNoInsuranceRadio = true,
  skuId,
}: InsuranceSelectorProps) {
  const site = getSite();
  const showTempMonthlyOfferText = site === BrandTypes.VERY && enableMonthlyProtectInsuranceTempOffer;
  const getChecked = (option: InsuranceOffer) => {
    if (typeof selectedInsurance === 'object' && !Object.is(selectedInsurance, null) && selectedInsurance?.offer) {
      return option.offer.productId === selectedInsurance?.offer.productId;
    }

    return false;
  };

  return options ? (
    <>
      <StyledTypography color="var(--color-global-black)" size="1" tag="p">
        Choose an Insurance option
      </StyledTypography>

      <RadioGroup dataTestId="insurance-radio-group" groupName={`insurance-radio-group${type}`}>
        <>
          {options.map((option) => {
            const maxWeeklyPrice = option?.offer.weeklyPrices?.length
              ? getInsuranceMaxWeeklyPrice(option.offer.weeklyPrices)
              : null;

            return (
              <InsuranceRadio
                className={option.offer.termDescription === 'Monthly Insurance' ? 'monthly-radio' : ''}
                dataTestId={`insurance-radio-button-${option.offer.termDescription
                  .toLocaleLowerCase()
                  .split(' ')
                  .join('-')}${type}`}
                clickCallback={() => {
                  handleSelectedInsurance(option);
                }}
                radioLabel={
                  <LabelTextContainer>
                    {option.offer.termDescription} £{option.offer.sellingPrice}
                    {option.offer.termDescription === 'Monthly Insurance' ? ' a month' : ''}
                    {site === BrandTypes.LITTLEWOODS && maxWeeklyPrice && (
                      <TypographyElement size="m1" tag="span" type="paragraph">
                        or {getPriceWithSymbol(maxWeeklyPrice.weeklySellingPrice)} per week interest free over{' '}
                        {maxWeeklyPrice.numberOfWeeks} weeks
                      </TypographyElement>
                    )}
                    {showTempMonthlyOfferText && option.offer.termDescription === 'Monthly Insurance' && (
                      <TypographyElement color="var(--color-brand-mid)" size="m1" tag="span" type="paragraph">
                        First 6 months FREE*
                      </TypographyElement>
                    )}
                  </LabelTextContainer>
                }
                radioName={option.offer.termDescription}
                key={`${option.offer.productId}${type}${option.offer.sellingPrice}${skuId}`}
                checked={getChecked(option)}
              />
            );
          })}

          {showNoInsuranceRadio && (
            <InsuranceRadio
              dataTestId={`insurance-radio-button-no-insurance${type}`}
              clickCallback={handleNoInsuranceSelect}
              radioLabel="No Insurance"
              radioName="No Insurance"
              key="no-insurance-radio"
              checked={selectedInsurance === 'No insurance'}
            />
          )}
        </>
      </RadioGroup>
    </>
  ) : null;
}

export default { InsuranceSelector };
